/** biome-ignore-all lint/suspicious/noArrayIndexKey: use better keys */
import {useCallback, useEffect, useState} from 'react';
import {useSelector, useStore} from 'react-redux';
import {ConfirmRemoveSectionModal} from '~/CreateFabricInvitation/routes/EventDetails/components/ConfirmRemoveSectionModal';
import {scrollElementIntoViewById} from '~/CreateFabricInvitation/utils/misc';
import {PollOptions} from '~/common/fabric/CreateDesign/components/Polls/PollOptions';
import {CREATE_EDIT_POLL} from '~/common/fabric/reducers/widgets';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {ResponsivePanel} from '~library/organisms/ResponsivePanel';
import {PlusIcon} from '~library/svg/v2/PlusIcon';

import {tanzanite} from '~libSass/v2/_exports.sass';
import './PollOverlay.sass';

const DEFAULT_VALIDATION_OBJ = {
  question: '',
  options: {},
};

const scrollIntoView = () => scrollElementIntoViewById('module-poll');

const findDuplicateOptions = (options) => {
  // Get a flat array of option names
  const optNameList = options.map((opt) => opt.option.trim());

  // Check if at-least one of the items passes the check. Returns true/false
  const dupSet = optNameList.reduce((acc, item, i) => {
    // If the found index is not the same as the current item's index, it's a duplicate
    const dupIndex = optNameList.indexOf(item);
    if (dupIndex !== i) {
      acc.add(i);
      acc.add(dupIndex);
    }
    return acc;
  }, new Set());
  return Array.from(dupSet);
};

export const PollOverlay = ({isOpen, onClose, overlayCallback, title, isMealOptions, hasPoll}) => {
  const minOptions = 2;
  const maxOptions = 6; // TODO add settings.POLL_OPTIONS_MAX_COUNT to BE context & use this everywhere
  const store = useStore();
  const eventWidgets = useSelector(({widgets}) => widgets);
  const pollId = hasPoll ? eventWidgets?.polls[0]?.id : undefined;
  const allowOther = hasPoll ? eventWidgets.polls[0].allow_other_option : false;
  const [questionValue, setQuestionValue] = useState('');
  const [optionValues, setOptionValues] = useState([]);
  const [validationErrors, setValidationErrors] = useState(DEFAULT_VALIDATION_OBJ);
  const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] = useState(false);
  const canRemove = optionValues.length > minOptions;
  const showAddOptionLink = optionValues.length < maxOptions;

  const handleAddOption = () => {
    const newArray = [...optionValues, {option: '', autofocus: true}];
    setOptionValues(newArray);
  };

  const savePoll = useCallback(
    (optionsArray) => {
      const pollWidget = {
        question: questionValue,
        // biome-ignore lint/style/useNamingConvention: existing error
        allow_other_option: allowOther,
        id: pollId,
        options: optionsArray,
      };
      store.dispatch({
        type: CREATE_EDIT_POLL,
        payload: {polls: [pollWidget]},
      });
      overlayCallback(pollWidget);
      onClose(!!pollWidget);
    },
    [allowOther, onClose, overlayCallback, pollId, questionValue, store]
  );

  const validateFields = useCallback(() => {
    const optionsArray = optionValues.filter((o) => !!o.option.trim());
    const errors = structuredClone(DEFAULT_VALIDATION_OBJ);
    if (!questionValue) errors.question = 'Poll question is required.';
    if (optionsArray.length < minOptions) {
      const emptyIndex = optionValues.findIndex((opt) => !opt.option.trim());
      if (emptyIndex !== -1) {
        errors.options[emptyIndex] = 'At least 2 response options are required.';
      }
    } else {
      const duplicateIndices = findDuplicateOptions(optionsArray);
      if (duplicateIndices.length > 0) {
        duplicateIndices.forEach((index) => {
          errors.options[index] = 'Two or more response options are the same.';
        });
      } else if (!errors.question && Object.keys(errors.options).length === 0) {
        savePoll(optionsArray);
        scrollIntoView();
      }
    }
    setValidationErrors(errors);
  }, [optionValues, questionValue, savePoll]);

  const handlePollSubmit = useCallback(() => {
    validateFields();
  }, [validateFields]);

  const handleConfirmRemovePoll = useCallback(() => {
    scrollIntoView();
    store.dispatch({type: CREATE_EDIT_POLL, payload: {polls: []}});
    onClose(false);
  }, [store, onClose]);

  const handleRemovePoll = useCallback(() => {
    setIsConfirmRemoveModalOpen(true);
  }, []);

  const onCloseConfirmRemoveModal = useCallback(() => {
    setIsConfirmRemoveModalOpen(false);
  }, []);

  const handleQuestionChange = useCallback((e) => {
    setQuestionValue(e.target.value);
    setValidationErrors((prev) => ({
      ...prev,
      question: e.target.value.trim() ? false : prev.question,
    }));
  }, []);

  const optionCallback = useCallback(
    (index, e, type) => {
      if (type === 'remove') {
        const filtered = optionValues.filter((o, i) => i !== index);
        setOptionValues(filtered);
      } else {
        setValidationErrors(structuredClone(DEFAULT_VALIDATION_OBJ));
        const newValues = [...optionValues];
        newValues[index] = {option: e.target.value};
        setOptionValues(newValues);
      }
    },
    [optionValues]
  );

  useEffect(() => {
    if (!isOpen) return;
    setValidationErrors(structuredClone(DEFAULT_VALIDATION_OBJ));
    setQuestionValue(hasPoll ? eventWidgets.polls[0].question : '');
    setOptionValues(hasPoll ? eventWidgets.polls[0].options : [{option: ''}, {option: ''}]);
  }, [isOpen, hasPoll, eventWidgets.polls]);

  const body = isMealOptions
    ? "Let's make sure everyone gets food they'll enjoy. Btw, you can update this at any time."
    : 'Looking for guest input? Add a poll to your Invitation to get their take on meals, activities, and more.';
  const questionLabel = isMealOptions
    ? 'Ask your guests what food they prefer...'
    : 'Ask your guests anything...';

  return (
    <ResponsivePanel
      isOpen={isOpen}
      onClose={() => onClose(hasPoll)}
      title={title}
      body={body}
      showCover
      modalId="poll"
      renderMobileViewAs="bottom-sheet"
      portalMode
      disableOverlayClick
    >
      <div className="poll__overlay">
        <Typography variant="paragraph2" color="asphalt">
          {body}
        </Typography>

        <div className="poll__overlay__poll-content">
          <div className="poll__overlay__question">
            <Typography variant="label2">
              {isMealOptions ? null : 'Poll '}
              Question*
            </Typography>
            <Input
              data-qa-id="poll-question-input"
              aria-label={questionLabel}
              id="question"
              name="question"
              type="text"
              placeholder={questionLabel}
              maxLength={140}
              required
              value={questionValue}
              error={validationErrors.question}
              onChange={handleQuestionChange}
              onBlur={() => setValidationErrors((prev) => ({...prev, question: ''}))}
            />
          </div>

          <div className="poll__overlay__response-options">
            <Typography variant="label2">Response Options</Typography>
            {optionValues.map((opt, i) => (
              <PollOptions
                key={`option-${i}`}
                optionCallback={optionCallback}
                opt={opt}
                optionNum={i + 1}
                canRemove={canRemove}
                minOptions={minOptions}
                index={i}
                isMealOptions={isMealOptions}
                optionValues={optionValues}
                setOptionValues={setOptionValues}
                error={validationErrors.options[i]}
              />
            ))}
          </div>

          {showAddOptionLink && (
            <Button
              onClick={handleAddOption}
              variant="link"
              data-qa-id="button_add-poll-item"
              type="button"
              className="poll__add-button"
            >
              <PlusIcon ratio={0.5} color={tanzanite} />
              Add another response option
            </Button>
          )}
        </div>
      </div>

      <div className="poll-overlay__ctas">
        <Button
          onClick={handlePollSubmit}
          data-qa-id="create-poll"
          variant="primary"
          size="large"
          type="button"
        >
          Save
        </Button>
        {hasPoll && (
          <Button
            onClick={handleRemovePoll}
            data-qa-id="remove-poll"
            variant="transparent"
            className="poll-overlay__remove-button"
            size="large"
            type="button"
          >
            Remove section
          </Button>
        )}
        {isConfirmRemoveModalOpen && (
          <ConfirmRemoveSectionModal
            isOpen
            onClose={onCloseConfirmRemoveModal}
            onConfirm={handleConfirmRemovePoll}
            qaId="confirm-remove-poll"
          />
        )}
      </div>
    </ResponsivePanel>
  );
};
